import * as React from "react";
import { createRoot } from "react-dom/client";
// import * as Server from "react-dom/server";
import { AFRICAN_COUNTRIES, AFRICAN_CULTURES } from "../backend/lib/constants";

import { useMuseumSearch } from "./hooks/useMuseumSearch";
import { MuseumObject } from "../backend/types/domain";
import Layout, { defaultSideBarSections, LayoutRef } from "./layout";
import { MuseumObjectDetail } from "./components/MuseumObjectDetail";
import { MuseumObjectCard, CardViewMode } from "./components/MuseumObjectCard";
import { SearchHero } from "./components/SearchHero";
import { CultureMapData } from "./components/CultureMap";
import africanCulturesExpanded from "../backend/lib/data/africanCulturesExpanded.json";
import {
  ChangelogModal,
  ChangelogMenuItem,
  useChangelogNotification,
} from "./components/Changelog";
import { MailingListCTARef } from "./components/MailingListCTA";
import { FilterProvider } from "./context/FilterContext";
import { cultureNameToData } from "./lib/cultureWikipedia";
import {
  useNavigationState,
  NavigationState,
  DEFAULT_KEYWORD_QUERY,
  resolveSemanticQuery,
} from "./hooks/useNavigationState";
import { TimelineRange } from "./components/TimelineScrubber";
import {
  TIMELINE_MAX_YEAR,
  TIMELINE_MIN_YEAR,
  isFullRange,
} from "../backend/lib/timeline";
import {
  ArtifactViewSource,
  initAnalytics,
  setArtifactViewSource,
  trackEvent,
} from "./lib/analytics";

const FULL_TIMELINE: TimelineRange = {
  from: TIMELINE_MIN_YEAR,
  to: TIMELINE_MAX_YEAR,
};

const DEFAULT_EMBEDDING_DISTANCE = 0.25;

const urlParams = new URLSearchParams(window.location.search);

const SHOW_SIDEBAR =
  true || urlParams?.get("show_sidebar")?.toLowerCase() === "true";
const ENABLE_GLOBE = urlParams?.get("globe")?.toLowerCase() === "true";

const getArtifactIdFromState = (state: NavigationState): string | null =>
  state.artifactId || null;

const MuseumSearch = () => {
  const {
    search,
    getAllObjects,
    fetchArtifactById,
    prependArtifact,
    loading,
    error,
    summary,
    results,
    clearResults,
    loadTimeline,
    timeline,
    timelineTotal,
    timelineLoading,
  } = useMuseumSearch();
  const navigationState = useNavigationState();
  const initialNavigationState = React.useMemo(
    () => navigationState.getState(),
    [navigationState],
  );
  const {
    filters: hashFilters,
    query: hashQuery,
    dateRange: hashDateRange,
  } = initialNavigationState;
  const [page, setPage] = React.useState(1);
  const [objectDetail, setObjectDetail] = React.useState<MuseumObject | null>(
    null,
  );
  const [deepLinkedArtifact, setDeepLinkedArtifact] =
    React.useState<MuseumObject | null>(null);
  const [imagesOnly, setImagesOnly] = React.useState(true);
  const [gridLayout, setGridLayout] = React.useState(true);
  const [cardViewMode, setCardViewMode] =
    React.useState<CardViewMode>("standard");
  const [loadedPages, setLoadedPages] = React.useState(new Set<number>());
  // New filter state for selected country — default to none (empty = show all via backend)
  const [selectedFilters, setSelectedFilters] = React.useState<string[]>(
    hashFilters ?? [],
  );
  const [filterSearch, setFilterSearch] = React.useState<string>("");
  const [resetMode, setResetMode] = React.useState<boolean>(true);
  const [keywordQuery, setKeywordQuery] = React.useState(
    hashQuery || DEFAULT_KEYWORD_QUERY,
  );
  const [showSearchHero, setShowSearchHero] = React.useState(true);
  const [isSearchHeroCompact, setIsSearchHeroCompact] = React.useState(false);
  const [dateRange, setDateRange] = React.useState<TimelineRange>(
    hashDateRange ?? FULL_TIMELINE,
  );
  const keywordQueryRef = React.useRef(keywordQuery);
  const selectedFiltersRef = React.useRef(selectedFilters);
  const dateRangeRef = React.useRef(dateRange);

  React.useEffect(() => {
    dateRangeRef.current = dateRange;
  }, [dateRange]);

  React.useEffect(() => {
    keywordQueryRef.current = keywordQuery;
  }, [keywordQuery]);

  React.useEffect(() => {
    selectedFiltersRef.current = selectedFilters;
  }, [selectedFilters]);

  // React.useEffect(() => {
  //   const handleScroll = () => {
  //     const nextIsCompact = window.scrollY > 120;
  //     setIsSearchHeroCompact(nextIsCompact);
  //     setShowSearchHero(window.scrollY < 520);
  //   };

  //   handleScroll();
  //   window.addEventListener("scroll", handleScroll, { passive: true });
  //   return () => window.removeEventListener("scroll", handleScroll);
  // }, []);

  // Changelog state
  const [isChangelogOpen, setIsChangelogOpen] = React.useState(false);
  const { hasUnseenChanges: hasUnseenChangelogs } = useChangelogNotification();
  const mailingListRef = React.useRef<MailingListCTARef>(null);
  const layoutRef = React.useRef<LayoutRef>(null);

  const performSearch = React.useCallback(
    ({
      page: nextPage = 1,
      prependResults = false,
      query,
      filters,
      range,
    }: {
      page?: number;
      prependResults?: boolean;
      query?: string;
      filters?: string[];
      range?: TimelineRange;
    } = {}) => {
      const resolvedFilters = filters ?? selectedFiltersRef.current;
      const resolvedInput = query ?? keywordQueryRef.current;
      const resolvedRange = range ?? dateRangeRef.current;
      const { resolvedQuery, semanticQuery } =
        resolveSemanticQuery(resolvedInput);
      // The whole axis is not a filter — leaving the bounds off keeps the
      // date predicate out of the SQL entirely rather than emitting one that
      // matches every row.
      const dateBounds = isFullRange(resolvedRange.from, resolvedRange.to)
        ? {}
        : { dateStart: resolvedRange.from, dateEnd: resolvedRange.to };
      search(
        {
          keywords: resolvedQuery,
          semanticQuery,
          embeddingDistance: DEFAULT_EMBEDDING_DISTANCE,
          page: nextPage,
          locations: resolvedFilters,
          ...dateBounds,
        },
        undefined,
        prependResults,
      );
      // Post search state updates
      // TODO: move this to the applySearchTransition function
      setPage(nextPage);
      setLoadedPages((prev) => new Set(prev).add(nextPage));
      navigationState.updateState({
        query: resolvedQuery,
        filters: resolvedFilters,
        dateRange: resolvedRange,
      });
    },
    [navigationState, search],
  );

  const applySearchTransition = React.useCallback(
    ({
      query = keywordQueryRef.current,
      filters = selectedFiltersRef.current,
      range = dateRangeRef.current,
      page: nextPage = 1,
      append = false,
      reset = false,
    }: {
      query?: string;
      filters?: string[];
      range?: TimelineRange;
      page?: number;
      append?: boolean;
      reset?: boolean;
    }) => {
      setKeywordQuery(query);
      setSelectedFilters(filters);
      setDateRange(range);

      keywordQueryRef.current = query;
      selectedFiltersRef.current = filters;
      dateRangeRef.current = range;

      if (reset || !append) {
        clearResults();
        setLoadedPages(new Set());
      }

      performSearch({
        page: nextPage,
        query,
        filters,
        range,
        prependResults: !append,
      });
    },
    [],
  );

  const handleShowDetail = (
    obj: MuseumObject | null,
    source: ArtifactViewSource = "card",
  ) => {
    if (obj) setArtifactViewSource(source);
    setObjectDetail(obj);
    navigationState.updateState(
      { artifactId: obj?.id || null },
      { replace: false },
    );
  };

  const handleCloseDetail = () => {
    setObjectDetail(null);
    setDeepLinkedArtifact(null);
    // Only clear the URL hash, do not scroll
    navigationState.updateState({ artifactId: null });
  };

  const handleSearchSubmit = () => {
    trackEvent("search_submit", { search_term: keywordQuery.trim() });
    applySearchTransition({
      query: keywordQuery,
      page: 1,
      reset: true,
    });
  };

  /**
   * The scrubber fires this on release. It searches immediately, like the map
   * does — a range you have to confirm with a second click is a range most
   * people never apply.
   */
  const handleTimelineCommit = React.useCallback(
    (range: TimelineRange) => {
      trackEvent("timeline_filter", {
        date_range: isFullRange(range.from, range.to)
          ? "all"
          : `${range.from} to ${range.to}`,
      });
      applySearchTransition({ range, page: 1, reset: true });
    },
    [applySearchTransition],
  );

  /**
   * Refresh the histogram whenever the non-date part of the query changes, so
   * the bars always describe the set being browsed. The date range is
   * deliberately absent from the dependencies: selecting a period must not
   * redraw the chart used to select it.
   */
  React.useEffect(() => {
    const { resolvedQuery, semanticQuery } = resolveSemanticQuery(keywordQuery);
    loadTimeline({
      keywords: resolvedQuery,
      semanticQuery,
      locations: selectedFilters,
    });
  }, [loadTimeline, keywordQuery, selectedFilters]);

  const handleFiltersSubmit = () => {
    applySearchTransition({
      filters: selectedFilters,
      page: 1,
      reset: true,
    });
  };

  /**
   * Handle filter by culture, where clicking on a particular culture on a card
   * or detail will load results for that culture only.
   * 
   * @param culture - The culture to filter by
   */
  const handleFilterByCulture = (culture: string) => {
    setFilterSearch(culture);
    handleCloseDetail();
    const locations = [
      ...(cultureNameToData[culture.toLowerCase().trim()]?.alternate_names ??
        []),
      culture,
    ];
    applySearchTransition({
      query: keywordQueryRef.current,
      filters: locations,
      page: 1,
      reset: true,
    });
  };

  /** All culture + country names for the CulturePicker pill list. */
  const allOptions = React.useMemo(() => {
    const cultures = (africanCulturesExpanded as CultureMapData[]).map(
      (c) => c.culture,
    );
    const unique = Array.from(new Set([...AFRICAN_COUNTRIES, ...cultures]));
    return unique.sort();
  }, []);

  /**
   * Toggle a culture/country pill in the filter list.
   * When adding a filter, strip any keyword tokens that match the option name
   * so the search query stays clean.
   */
  const handleTogglePill = React.useCallback(
    (option: string) => {
      const isSelected = selectedFiltersRef.current.includes(option);
      trackEvent("pill_filter", {
        culture: option,
        filter_action: isSelected ? "remove" : "add",
      });
      const nextFilters = isSelected
        ? selectedFiltersRef.current.filter((f) => f !== option)
        : [...selectedFiltersRef.current, option];

      let nextQuery = keywordQueryRef.current;
      if (!isSelected) {
        // Strip tokens from the query that overlap with the option name
        const tokens = keywordQueryRef.current.split(/\s+/).filter(Boolean);
        const optLower = option.toLowerCase();
        const remaining = tokens.filter((token) => {
          const t = token.toLowerCase();
          return !optLower.includes(t) && !t.includes(optLower);
        });
        nextQuery =
          remaining.length > 0 ? remaining.join(" ") : DEFAULT_KEYWORD_QUERY;
      }

      applySearchTransition({
        filters: nextFilters,
        query: nextQuery,
        page: 1,
        reset: true,
      });
    },
    [applySearchTransition],
  );

  const culturesData = africanCulturesExpanded as CultureMapData[];

  const culturesToHighlight = React.useMemo(
    () =>
      new Set(
        culturesData
          .filter((c) => {
            const matchesCulture = selectedFilters.includes(c.culture);
            const matchesAlternate = c.alternate_names?.some((name) =>
              selectedFilters.includes(name),
            );
            return matchesCulture || matchesAlternate;
          })
          .map((c) => c.culture),
      ),
    [selectedFilters, culturesData],
  );

  const handleMapFilterToggle = React.useCallback(
    (culture: CultureMapData) => {
      const filtersToToggle = [
        culture.culture,
        ...(culture.alternate_names || []),
      ];
      const isSelected = filtersToToggle.some((f) =>
        selectedFilters.includes(f),
      );
      trackEvent("map_filter", {
        culture: culture.culture,
        filter_action: isSelected ? "remove" : "add",
      });
      const nextFilters = isSelected
        ? selectedFilters.filter((f) => !filtersToToggle.includes(f))
        : [...selectedFilters, ...filtersToToggle];

      applySearchTransition({ filters: nextFilters, page: 1, reset: true });
    },
    [selectedFilters, applySearchTransition],
  );

  const objects = getAllObjects();
  const totalResults = summary?.totalResults;
  const hasMore =
    objects?.length > 0 &&
    (!!totalResults ? objects?.length <= totalResults : true);
  const objectsToShow = objects.filter((obj) =>
    imagesOnly ? obj?.images?.length && obj?.images?.length > 0 : true,
  );

  const currentDetailIndex = objectDetail
    ? objectsToShow.findIndex((o) => o.id === objectDetail.id)
    : -1;

  const handlePrevDetail = () => {
    if (currentDetailIndex > 0) {
      handleShowDetail(objectsToShow[currentDetailIndex - 1], "prev");
    }
  };

  const handleNextDetail = () => {
    if (currentDetailIndex < objectsToShow.length - 1) {
      handleShowDetail(objectsToShow[currentDetailIndex + 1], "next");
    }
  };

  // Setup deep linking on initial page loda
  React.useEffect(
    () => {
      const artifactId = getArtifactIdFromState(initialNavigationState);
      if (artifactId) {
        fetchArtifactById(artifactId).then((artifact) => {
          if (artifact) {
            setArtifactViewSource("deep_link");
            setDeepLinkedArtifact(artifact);
            setObjectDetail(artifact);
            prependArtifact(artifact);
          }
        });
      }

      performSearch({ page: 1 });

      // Handle changes to the deep link in the URL
      const handleHashChange = (state: NavigationState) => {
        const { artifactId, query, filters } = state;

        if (artifactId) {
          fetchArtifactById(artifactId).then((artifact) => {
            if (artifact) {
              setObjectDetail(artifact);
            }
          });
        } else {
          setObjectDetail(null);
          setDeepLinkedArtifact(null);
        }

        const nextQuery = query || DEFAULT_KEYWORD_QUERY;
        const nextFilters = filters || selectedFiltersRef.current;
        const currentQuery = keywordQueryRef.current;
        const currentFilters = selectedFiltersRef.current;
        const queryChanged = nextQuery !== currentQuery;
        const filtersChanged =
          filters &&
          (filters.length !== currentFilters.length ||
            filters.some((filter) => !currentFilters.includes(filter)));

        if (queryChanged || filtersChanged) {
          applySearchTransition({
            query: nextQuery,
            filters: nextFilters,
            page: 1,
            reset: true,
          });
        }
      };

      const unsubscribe = navigationState.subscribe(handleHashChange);
      return () => unsubscribe();
    },
    [
      // TODO: why does adding this in cause errors?
      // applySearchTransition,
      // fetchArtifactById,
      // getRandomPage,
      // initialNavigationState,
      // navigationState,
      // performSearch,
      // prependArtifact,
    ],
  );

  const mainContent = (
    <div>
      {showSearchHero && (
        <div className="search-hero-wrapper">
          <SearchHero
            keywordQuery={keywordQuery}
            onKeywordQueryChange={setKeywordQuery}
            onSearch={handleSearchSubmit}
            isCompact={isSearchHeroCompact}
            allOptions={allOptions}
            selectedFilters={selectedFilters}
            onTogglePill={handleTogglePill}
            onClearFilters={() => {
              trackEvent("filters_clear");
              applySearchTransition({ filters: [], page: 1, reset: true });
            }}
            culturesData={culturesData}
            culturesToHighlight={culturesToHighlight}
            filterSearch={filterSearch}
            onMapFilterToggle={handleMapFilterToggle}
            enableGlobe={ENABLE_GLOBE}
            timelineBuckets={timeline}
            timelineTotal={timelineTotal}
            timelineRange={dateRange}
            onTimelineCommit={handleTimelineCommit}
            timelineLoading={timelineLoading}
          />
        </div>
      )}
      {error && <p style={{ color: "red" }}>Error: {error}</p>}
      <div
        className={`${gridLayout ? "su-card-grid" : "su-card-row"} su-card-grid--${cardViewMode}`}
      >
          {objectsToShow?.length
            ? objectsToShow.map((obj, idx) => (
                <MuseumObjectCard
                  key={idx}
                  obj={obj}
                  showDetail={handleShowDetail}
                  viewMode={cardViewMode}
                />
              ))
            : !loading && (
                <div className="col-span-full flex items-center justify-center min-h-[100px] w-full bg-theme-color">
                  <p className="text-center">
                    No objects to show ({objects?.length} hidden)
                  </p>
                </div>
              )}
        </div>
      {objectDetail && (
        <MuseumObjectDetail
          obj={objectDetail}
          onClose={handleCloseDetail}
          onPrev={handlePrevDetail}
          onNext={handleNextDetail}
          hasPrev={currentDetailIndex > 0}
          hasNext={currentDetailIndex < objectsToShow.length - 1}
        />
      )}

      {/* Status indicator */}
      {objects.length > 0 && (
        <div className="text-center text-gray-600 py-2">
          Showing {objectsToShow.length}{" "}
          {/*totalResults && `of ${totalResults} total`*/} indexed artifacts
          {objectsToShow.length < objects.length &&
            ` (${objects.length - objectsToShow.length} hidden)`}
        </div>
      )}

      {/* Loading trigger for infinite scroll */}
      <div className="flex items-center justify-center p-4">
        <button
          onClick={(_) => {
            trackEvent("load_more", { results_page: page + 1 });
            applySearchTransition({
              page: page + 1,
              append: true,
              reset: false,
            });
          }}
          className={`border-theme-color text-theme-color hover:bg-theme-color border p-2 border-dashed font-bold ${
            loading ? "animate-pulse opacity-60 cursor-not-allowed" : ""
          }`}
          disabled={loading}
        >
          {loading ? "LOADING MORE ARTIFACTS..." : "LOAD MORE"}
        </button>
      </div>
    </div>
  );

  return (
    <FilterProvider onFilterByCulture={handleFilterByCulture}>
      <Layout
        ref={layoutRef}
        isDarkMode={false}
        title="African Artifacts Index"
        sideBarSections={[]}
        menuBarSections={[
          {
            label: "FILE",
            items: [
              {
                label: (
                  <div className="flex flex-row">
                    SEARCH
                    <p
                      className="font-bold cursor-pointer rotate-45 px-1"
                      title={`Search`}
                    >
                      ⚲
                    </p>
                  </div>
                ),
                name: "SEARCH",
                onClick: () => {
                  // Scroll to the top of the page and select the #keyword-search-hero-input element
                  window.scrollTo({ top: 0, behavior: "smooth" });
                  requestAnimationFrame(() => {
                    document.getElementById("keyword-search-hero-input")?.focus();
                  });
                },
              },
              {
                label: "RESET",
                onClick: () => {
                  // INSERT_YOUR_CODE
                  // Select all cultures + countries on reset
                  const resetFilters = [
                    ...AFRICAN_COUNTRIES,
                    ...AFRICAN_CULTURES,
                  ];
                  applySearchTransition({
                    query: DEFAULT_KEYWORD_QUERY,
                    filters: resetFilters,
                    page: 1,
                    reset: true,
                  });
                },
              },
            ],
          },
          {
            label: "VIEW",
            items: [
              {
                label: imagesOnly ? "SHOW ALL" : "SHOW ONLY IMAGES",
                onClick: () => {
                  setImagesOnly((old) => !old);
                },
              },
              {
                label: `LAYOUT: ${gridLayout ? "GRID" : "FLOAT"}`,
                onClick: () => {
                  setGridLayout((old) => !old);
                },
              },
              {
                label: `CARDS: ${cardViewMode.toUpperCase()}`,
                onClick: () => {
                  setCardViewMode((old) =>
                    old === "compact"
                      ? "standard"
                      : old === "standard"
                        ? "detailed"
                        : "compact",
                  );
                },
              },
            ],
          },
          {
            label: "ABOUT",
            items: [
              {
                label: "INTRO",
                onClick: () => {
                  window.open("https://suruleredotdev.substack.com/p/indexing-african-artifacts", "_blank");
                },
              },
              {
                label: (
                  <ChangelogMenuItem
                    onClick={() => setIsChangelogOpen(true)}
                    hasNotification={hasUnseenChangelogs}
                  />
                ),
                name: "CHANGELOG",
                onClick: () => setIsChangelogOpen(true),
              },
            ],
          },
        ]}
        showSidebar={SHOW_SIDEBAR}
        mailingListRef={mailingListRef}
      >
        {mainContent}
        <ChangelogModal
          isOpen={isChangelogOpen}
          onClose={() => setIsChangelogOpen(false)}
          onSubscribe={() => mailingListRef.current?.open()}
        />
      </Layout>
    </FilterProvider>
  );
};
const App = () => <MuseumSearch></MuseumSearch>;
initAnalytics();
const root = createRoot(document.getElementById("app")!);

root.render(<App />);
